import { useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/uk'
import 'dayjs/locale/de'
import 'dayjs/locale/fr'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { localiseUrl as localiseUrlFn } from 'helpers/localiseUrl'
import { TranslationContext } from 'helpers/withTranslations'
import { useTranslation } from 'hooks/useTranslation'
import { localeToCountry } from '../components/essential/SiteSettings/utils'
import type {
  LocaliseUrlOptions,
  LacedAvailableCurrencies,
  LacedAvailableLocales,
  Location,
  CurrencyObject,
  LocaleObject
} from 'types/localisation'

dayjs.extend(localizedFormat)

export const useLocale = (): LocaleData => {
  const { t } = useTranslation()
  const { locale, currency, location, availableLocales, availableCurrencies } =
    useContext(TranslationContext)

  const [localesImported, setLocalesImported] = useState<string[]>([])

  useEffect(() => {
    availableLocales.forEach((locale: LacedAvailableLocales) => {
      if (!localesImported.includes(locale)) {
        setLocalesImported([...localesImported, locale])
      }
    })
  }, [availableLocales, localesImported])

  useEffect(() => {
    dayjs.locale(locale)
  }, [locale])

  const localeObject = {} as LocaleObject

  for (const currentLocale of availableLocales) {
    localeObject[currentLocale] = {
      language: t(`shared.locales.language.${currentLocale}`),
      deliveryCountry: localeToCountry[currentLocale]
    }
  }

  const currencyObject = {} as CurrencyObject

  for (const currentCurrency of availableCurrencies) {
    currencyObject[currentCurrency] = {
      name: t(`shared.locales.currency_name.${currentCurrency}`),
      symbol: t(`shared.locales.currency.${currentCurrency}`)
    }
  }

  const localiseUrl = (
    url: string,
    options: LocaliseUrlOptions = {
      localeAsParam: false
    }
  ): string =>
    localiseUrlFn(url, {
      locale,
      availableLocales,
      ...options
    })

  return {
    availableLocales,
    locale,
    location,
    currency,
    localiseUrl,
    currencyObject,
    localeObject,
    dayjs
  }
}

interface LocaleData {
  availableLocales: LacedAvailableLocales[]
  locale: LacedAvailableLocales
  currency: LacedAvailableCurrencies
  location: Location
  localiseUrl: (url: string, options?: LocaliseUrlOptions) => string
  localeObject: LocaleObject
  currencyObject: CurrencyObject
  dayjs: any
}
